import React, { useMemo, useState } from "react";
import { ArrowRight, Cloud, Mail, UserRound } from "lucide-react";
import {
  SEX_OPTIONS,
  emptyParticipantIdentity,
  loadParticipantIdentity,
  participantIdentityValid,
  saveParticipantIdentity,
} from "@/components/assessment/participantIdentity";
import { loadCachedCloudAccount } from "@/components/account/localProfileData";

const fieldClass =
  "mt-2 block w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm forr-text-ink focus:outline-none focus:ring-2 focus:ring-teal-500/30";

const ParticipantIdentityGate = ({ children }) => {
  const account = useMemo(() => loadCachedCloudAccount(), []);
  const saved = useMemo(() => loadParticipantIdentity(), []);
  const accountEmail = String(account?.email || "").toLowerCase();
  const initialIdentity = useMemo(
    () => ({
      ...(saved || emptyParticipantIdentity()),
      email: accountEmail || saved?.email || "",
    }),
    [accountEmail, saved],
  );
  const [identity, setIdentity] = useState(initialIdentity);
  const [complete, setComplete] = useState(
    Boolean(saved && participantIdentityValid(initialIdentity)),
  );
  const [error, setError] = useState("");

  const update = (key, value) =>
    setIdentity((current) => ({ ...current, [key]: value }));

  const submit = (event) => {
    event.preventDefault();
    const candidate = {
      ...identity,
      email: accountEmail || identity.email,
    };
    if (!participantIdentityValid(candidate)) {
      setError(
        "Enter your name, preferred name, age (18+), and the sex response used for your physiological profile.",
      );
      return;
    }
    const next = saveParticipantIdentity(candidate);
    if (!next) {
      setError("Your profile could not be saved. Review the information and try again.");
      return;
    }
    setIdentity(next);
    setError("");
    setComplete(true);
  };

  if (complete) return children;

  return (
    <div className="min-h-screen forr-canvas forr-text-ink">
      <section className="relative overflow-hidden px-5 py-12 sm:px-8 sm:py-20">
        <div className="absolute -top-48 left-1/2 h-[720px] w-[720px] -translate-x-1/2 forr-radial-teal opacity-60" />
        <div className="relative mx-auto max-w-3xl">
          <div className="inline-flex items-center gap-3">
            <span className="forr-hairline" />
            <span className="forr-overline">Before the assessment</span>
          </div>
          <h1 className="mt-6 font-display text-4xl font-medium tracking-[-0.035em] forr-text-ink sm:text-6xl">
            Create your Adaptive Profile.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed forr-text-soft sm:text-lg">
            Your name personalizes the assessment and APOS Companion. Age and sex provide adult exercise and recovery context. Your account email connects this profile to secure cloud storage and is not sent to Adaptive Companion AI during ordinary conversations.
          </p>

          <form
            onSubmit={submit}
            className="mt-9 rounded-[28px] p-6 forr-surface sm:p-8"
            style={{ border: "1px solid var(--forr-line)" }}
            data-testid="participant-identity-gate"
          >
            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 items-center justify-center rounded-2xl"
                style={{ backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)" }}
              >
                <UserRound size={21} />
              </div>
              <div>
                <div className="forr-overline">Personal profile</div>
                <h2 className="mt-1 font-display text-2xl forr-text-ink">
                  Tell us who this profile belongs to
                </h2>
              </div>
            </div>

            <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <label className="text-sm forr-text-soft">
                First name
                <input
                  required
                  autoComplete="given-name"
                  value={identity.firstName}
                  onChange={(event) => update("firstName", event.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Last name
                <input
                  required
                  autoComplete="family-name"
                  value={identity.lastName}
                  onChange={(event) => update("lastName", event.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Name Companion should use
                <input
                  required
                  autoComplete="nickname"
                  value={identity.preferredName}
                  onChange={(event) => update("preferredName", event.target.value)}
                  placeholder={identity.firstName || "Preferred name"}
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Account email
                <span className="relative block">
                  <Mail size={16} className="pointer-events-none absolute left-4 top-[22px] forr-text-mute" />
                  <input
                    required
                    readOnly={Boolean(accountEmail)}
                    type="email"
                    autoComplete="email"
                    value={accountEmail || identity.email}
                    onChange={(event) => update("email", event.target.value)}
                    className={`${fieldClass} pl-11 ${accountEmail ? "cursor-not-allowed bg-slate-50" : ""}`}
                  />
                </span>
                {accountEmail && (
                  <span className="mt-2 flex items-center gap-1.5 text-xs forr-text-mute">
                    <Cloud size={13} /> Linked to your cloud account
                  </span>
                )}
              </label>
              <label className="text-sm forr-text-soft">
                Current age
                <input
                  required
                  type="number"
                  inputMode="numeric"
                  min="18"
                  max="120"
                  step="1"
                  value={identity.age}
                  onChange={(event) => update("age", event.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="text-sm forr-text-soft">
                Sex used for physiological context
                <select
                  required
                  value={identity.sex}
                  onChange={(event) => update("sex", event.target.value)}
                  className={fieldClass}
                >
                  <option value="">Select</option>
                  {SEX_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <p className="mt-5 text-xs leading-relaxed forr-text-mute">
              Choosing “Prefer not to say” keeps the assessment available and uses sex-neutral explanatory language. This adult assessment requires an age of 18 or older.
            </p>

            {error && (
              <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold forr-btn-primary sm:w-auto"
            >
              Continue to assessment <ArrowRight size={17} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ParticipantIdentityGate;
